import { getConfigForImportedTest, getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import {
  getDerivedFromMeasurementsConfigForPortionMassMeasurement,
  getDerivedFromMeasurementsConfigForPortionSieving,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/derivedFromMeasurementsConfig';
import { pathAsArray, pathAsString } from 'Lib/Object/Path/shared';
import * as oPath from '../../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../../QestIds';
import { getSeparationPath, getSeparationSieveSizeIds } from '../../../Helpers';
import {
  getMeasuredUsingConfigForPortionMassMeasurement,
  getMeasuredUsingConfigForPortionSieving,
} from '../../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.1 g.
 */
const massConfig = { uom: 'g', format: { nearest: 0.1 } };

const getConfig = (prefix: string) => {
  const maxSeparations = 2;
  let config = {
    ...getPortionMassesConfig(`${prefix}.WholePortion.Masses`, 'Portion'),
    ...getPortionSievingConfig(prefix, `${prefix}.WholePortion.Sieving`),
    ...getPortionWashingConfig(`${prefix}.WholePortion.Washing`),
    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },
    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
  };
  // This is fine...
  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion.Masses`, 'Coarse Portion'),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.CoarsePortion.Sieving`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion.Washing`),
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Masses`, 'Fine Portion'),
      ...getPortionMassesConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Masses`, 'Subspecimen'),
      ...getPortionSievingConfig(prefix, `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Sieving`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen.Washing`),
    }))
  );

  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string, materialLabel: string) => ({
  [`${prefix}.WetMass`]: massConfig,
  [`${prefix}.DryMass`]: massConfig,
  ...getConfigForMeasuredTest(qestIds.moistureContent.ASTM_D_2216.rev2019, `${prefix}.AssociatedMoistureContent`, {
    materialLabelOverride: materialLabel,
  }),
  ...getPortionMassMeasurementConfig(`${prefix}.MassMeasurement`),
  ...getConfigForImportedTest(qestIds.moistureContent.ASTM_D_2216.rev2019, `${prefix}.AssociatedMoistureContent`),
  //Should be based on test method, but this is not available. See QP-3830.
  [`${prefix}.MoistureContent`]: {
    format: { nearest: 0.1 },
  },
});

const getPortionWashingConfig = (prefix: string) =>
  mergeConfig({
    [`${prefix}.WashedDryMass`]: massConfig,
    [`${prefix}.WashingsDryMass`]: massConfig,
    ...getConfigForMeasuredTest(qestIds.wash.ASTM_D6913.rev2017, `${prefix}.AssociatedWashTest`),
  });

//Note that the prefix parameter should already contain the root prefix within, we just needed the root separately
const getPortionSievingConfig = (rootPrefix: string, portionSievingPath: string) =>
  mergeConfig(
    {
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Width`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Length`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionSievingPath}.Equipment.SieveSetDimensions.Diameter`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionSievingPath}.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionSievingPath}.RepresentedDryMass`]: massConfig,
      [`${portionSievingPath}.CorrectionFactor`]: { format: { nearest: 0.1 } },
      [`${portionSievingPath}.PortionLoss`]: { format: { nearest: 0.01 } },
      [`${portionSievingPath}.FractionalPercentRetainedOnUpperLimitSieve`]: { format: { nearest: 0.01 } },
      [`${portionSievingPath}.Records.MaximumIndividualMassRetained`]: massConfig,
      [`${portionSievingPath}.Records.PercentagePassing`]: (configArgs: IConfigFunctionArgs) => {
        const method = oPath.get(configArgs.worksheet, `${rootPrefix}.Preparation.Method`);
        const sieveSizeId = oPath.get(
          configArgs.worksheet,
          pathAsString([...pathAsArray(configArgs.name).slice(0, -1), 'SieveSize'])
        )?.Id;
        let precision = method === 'A' ? 1 : 0.1;
        if (getSeparationSieveSizeIds(configArgs.worksheet).includes(sieveSizeId)) precision /= 10;
        return { format: { nearest: precision } };
      },
    },
    getMeasuredUsingConfigForPortionSieving(portionSievingPath),
    getDerivedFromMeasurementsConfigForPortionSieving(portionSievingPath),
    {
      [`${portionSievingPath}.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

const getPortionMassMeasurementConfig = (prefix: string) =>
  mergeConfig(
    getMeasuredUsingConfigForPortionMassMeasurement(prefix),
    getDerivedFromMeasurementsConfigForPortionMassMeasurement(prefix),
    {
      [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${prefix}.Equipment.DryMassBalance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

export { getConfig };

export default {
  [qestIds.psdSieveAnalysis.ASTM_D6913.rev2017]: getConfig,
};
